<template>
  <div class="fuel-list">
    <!-- Header with Actions -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 mt-2">
      <div>
        <h2 class="text-3xl font-extrabold text-gray-900 tracking-tight">Fuel Transactions</h2>
        <p v-if="selectedEquipmentName" class="text-sm text-indigo-600 font-medium mt-1 transition-all duration-300">
          <font-awesome-icon icon="gas-pump" class="mr-1" />
          Showing data for: <span class="bg-indigo-50 px-2 py-0.5 rounded">{{ selectedEquipmentName }}</span>
        </p>
        <p v-else class="text-sm text-gray-500 mt-1">Manage and track your fleet's fuel consumption</p>
      </div>
      
      <div class="flex flex-wrap items-center gap-3 w-full md:w-auto">
        <!-- Action Group -->
        <div class="flex items-center bg-gray-100 p-1 rounded-lg shadow-sm border border-gray-200">
          <button 
            @click="showFilters = !showFilters" 
            class="flex items-center px-4 py-2 rounded-md transition-all duration-300"
            :class="showFilters ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'"
            title="Toggle Filters"
          >
            <font-awesome-icon icon="filter" :class="{ 'mr-2': true, 'text-indigo-500': showFilters }" />
            <span class="text-sm font-semibold">{{ showFilters ? 'Hide' : 'Filter' }}</span>
          </button>
          
          <div class="w-px h-6 bg-gray-300 mx-1"></div>
          
          <button 
            @click="viewMode = viewMode === 'grid' ? 'table' : 'grid'" 
            class="flex items-center px-4 py-2 rounded-md transition-all duration-300 text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm"
            :title="viewMode === 'grid' ? 'Switch to Table' : 'Switch to Grid'"
          >
            <font-awesome-icon :icon="viewMode === 'grid' ? 'table' : 'grip-horizontal'" class="mr-2" />
            <span class="text-sm font-semibold">{{ viewMode === 'grid' ? 'Table' : 'Grid' }}</span>
          </button>
        </div>

        <button 
          @click="handleAddTransaction" 
          class="flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-300 transform active:scale-95 flex-grow md:flex-grow-0"
        >
          <font-awesome-icon icon="plus" class="mr-2" />
          Add Transaction
        </button>
      </div>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <StatsCard
        title="Total Fuel"
        :value="`${localTotalFuelUsed.toFixed(1)}L`"
        icon="\u26fd"
        trend="up"
      />
      <StatsCard
        title="Total Cost"
        :value="`tsh${localTotalFuelCost.toFixed(2)}`"
        icon="\ud83d\udcb0"
        trend="up"
      />
      <StatsCard
        title="Avg Cost/L"
        :value="`tsh${localAverageCostPerLiter.toFixed(2)}`"
        icon="\ud83d\udcca"
        trend="neutral"
      />
    </div>

    <!-- Filters -->
    <div v-if="showFilters" class="bg-white rounded-lg shadow-sm p-4 mb-6">
      <h3 class="text-lg font-semibold mb-4">Filters</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Equipment</label>
          <select
            v-model="filters.equipment_id"
            class="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="">All Equipment</option>
            <option v-for="eq in fuelEquipment" :key="eq.id" :value="eq.id">
              {{ eq.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <select v-model="filters.date_range" class="w-full p-2 border border-gray-300 rounded-md">
            <option value="">All Dates</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <select v-model="filters.sort_by" class="w-full p-2 border border-gray-300 rounded-md">
            <option value="date_desc">Date (Newest First)</option>
            <option value="date_asc">Date (Oldest First)</option>
            <option value="cost_desc">Cost (Highest First)</option>
            <option value="cost_asc">Cost (Lowest First)</option>
          </select>
        </div>
      </div>
      <div class="flex justify-end space-x-2 mt-4">
        <button @click="applyFilters" class="btn-primary">Apply Filters</button>
        <button @click="resetFilters" class="btn-secondary">Reset</button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <LoadingSpinner class="mx-auto" />
      <p class="text-gray-600 mt-2">Loading fuel transactions...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div class="flex items-center">
        <span class="text-red-600">⚠️</span>
        <p class="text-red-800 ml-2">{{ error }}</p>
      </div>
      <button @click="onRetryFetch" class="btn-secondary mt-2">Try Again</button>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredTransactions.length === 0"
      class="text-center py-16 bg-white border-2 border-dashed border-gray-200 rounded-2xl"
    >
      <div class="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
        <font-awesome-icon icon="gas-pump" class="text-3xl text-gray-300" />
      </div>
      <h3 class="text-xl font-bold text-gray-800 mb-2">No fuel transactions found</h3>
      <p class="text-gray-500 max-w-xs mx-auto">
        There are no transactions matching your current filters. Adjust them or add a new record.
      </p>
    </div>

    <!-- Transactions Grid or Table -->
    <div v-else>
      <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <FuelCard
          v-for="transaction in paginatedTransactions"
          :key="transaction.id"
          :transaction="transaction"
          @edit="handleEdit"
          @view="handleView"
          @delete="handleDelete"
        />
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm overflow-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Equipment
              </th>
              <th
                class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Liters
              </th>
              <th
                class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Cost
              </th>
              <th
                class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Total
              </th>
              <th
                class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="tx in paginatedTransactions" :key="tx.id">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ new Date(tx.transaction_date).toLocaleDateString() }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {{ tx.equipment_name || tx.equipment?.name || equipmentStore.getName(tx.equipment_id) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                {{ Number(tx.liters || 0).toFixed(1) }}L
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                tsh{{ Number(tx.cost_per_liter || 0).toFixed(2) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                tsh{{ Number(tx.total_cost || tx.liters * tx.cost_per_liter || 0).toFixed(2) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                <div class="flex items-center justify-center space-x-3">
                  <button
                    @click="handleEdit(tx)"
                    class="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                    title="Edit"
                  >
                    <font-awesome-icon icon="edit" />
                  </button>
                  <button
                    @click="handleView(tx)"
                    class="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                    title="View"
                  >
                    <font-awesome-icon icon="eye" />
                  </button>
                  <button 
                    @click="handleDelete(tx)" 
                    class="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    title="Delete"
                  >
                    <font-awesome-icon icon="trash-alt" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredTransactions.length > 0" class="mt-6 flex justify-between items-center">
      <div class="text-sm text-gray-700">
        Showing {{ pageStart }}-{{ pageEnd }} of {{ filteredTransactions.length }} transactions
      </div>
      <Pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="handlePageChange"
      />
    </div>

    <!-- Fuel Form Modal -->
    <FuelForm
      v-if="showForm"
      :transaction="editingTransaction ?? undefined"
      :loading="formLoading"
      @save="handleSave"
      @cancel="showForm = false"
    />

    <!-- View Transaction Modal -->
    <Modal v-if="showViewModal" :is-open="true" @close="showViewModal = false">
      <template #header>
        <h3 class="text-lg font-semibold">Fuel Transaction Details</h3>
      </template>
      <div class="space-y-3">
        <p>
          <strong>Date:</strong>
          {{ viewTransaction ? new Date(viewTransaction.transaction_date).toLocaleString() : '' }}
        </p>
        <p>
          <strong>Equipment:</strong>
          {{
            viewTransaction?.equipment_name ||
            viewTransaction?.equipment?.name ||
            (viewTransaction ? equipmentStore.getName(viewTransaction.equipment_id) : '')
          }}
        </p>
        <p>
          <strong>Liters:</strong>
          {{ viewTransaction ? Number(viewTransaction.liters || 0).toFixed(1) + 'L' : '' }}
        </p>
        <p>
          <strong>Cost per Liter:</strong> tsh{{
            viewTransaction ? Number(viewTransaction.cost_per_liter || 0).toFixed(2) : ''
          }}
        </p>
        <p>
          <strong>Total Cost:</strong> tsh{{
            viewTransaction
              ? Number(
                  viewTransaction.total_cost ||
                    viewTransaction.liters * viewTransaction.cost_per_liter ||
                    0,
                ).toFixed(2)
              : ''
          }}
        </p>
        <div v-if="viewTransaction?.notes">
          <strong>Notes:</strong>
          <p class="italic">"{{ viewTransaction?.notes }}"</p>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useFuelStore } from '@/stores/fuelStore'
import { useEquipmentStore } from '@/stores/equipmentStore'
import { useNotificationStore } from '@/stores/notification'
import { useConfirm } from '@/stores/confirm'
import type { FuelTransaction, FuelTransactionCreate } from '@/types/fuel'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faPlus,
  faFilter,
  faTable,
  faGripHorizontal,
  faGasPump,
  faEdit,
  faEye,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import FuelCard from './FuelCardReal.vue'
import FuelForm from './FuelTransactionForm.vue'
import Modal from '@/components/shared/Modal.vue'
import StatsCard from '@/components/shared/StatsCard.vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import Pagination from '@/components/shared/Pagination.vue'

library.add(faPlus, faFilter, faTable, faGripHorizontal, faGasPump, faEdit, faEye, faTrashAlt)

const fuelStore = useFuelStore()
const equipmentStore = useEquipmentStore()
const notificationStore = useNotificationStore()
const { confirm } = useConfirm()

const showFilters = ref(false)
const viewMode = ref<'grid' | 'table'>('grid')
const filters = ref({
  equipment_id: '',
  // empty = all dates (show everything by default)
  date_range: '',
  sort_by: 'date_desc',
})
const loading = ref(false)
const error = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const showForm = ref(false)
const editingTransaction = ref<null | FuelTransaction>(null)
const formLoading = ref(false)
const showViewModal = ref(false)
const viewTransaction = ref<FuelTransaction | null>(null)

onMounted(async () => {
  await equipmentStore.fetchEquipment()
  await fuelStore.fetchTransactions()
})

const fuelEquipment = computed(() => equipmentStore.equipment || [])

const filteredTransactions = computed(() => {
  let list = Array.isArray(fuelStore.transactions) ? fuelStore.transactions.slice() : []

  // equipment filter
  if (filters.value.equipment_id) {
    const eqId = Number(filters.value.equipment_id)
    if (!Number.isNaN(eqId)) list = list.filter((t) => t.equipment_id === eqId)
  }

  // date range filter (last N days)
  if (filters.value.date_range) {
    const days = Number(filters.value.date_range) || 30
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - days)
    list = list.filter((t) => new Date(t.transaction_date) >= cutoff)
  }

  // sort
  switch (filters.value.sort_by) {
    case 'date_asc':
      list.sort(
        (a, b) => new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime(),
      )
      break
    case 'cost_asc':
      list.sort(
        (a, b) =>
          (a.total_cost || a.liters * a.cost_per_liter) -
          (b.total_cost || b.liters * b.cost_per_liter),
      )
      break
    case 'cost_desc':
      list.sort(
        (a, b) =>
          (b.total_cost || b.liters * b.cost_per_liter) -
          (a.total_cost || a.liters * a.cost_per_liter),
      )
      break
    default:
      list.sort(
        (a, b) => new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime(),
      )
  }

  return list
})

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredTransactions.value.length / itemsPerPage.value)),
)

const paginatedTransactions = computed(() => {
  const list = Array.isArray(filteredTransactions.value) ? filteredTransactions.value : []
  const start = (currentPage.value - 1) * itemsPerPage.value
  return list.slice(start, start + itemsPerPage.value)
})

const pageStart = computed(() => {
  const total = filteredTransactions.value.length
  if (total === 0) return 0
  return (currentPage.value - 1) * itemsPerPage.value + 1
})

const pageEnd = computed(() =>
  Math.min(filteredTransactions.value.length, currentPage.value * itemsPerPage.value),
)

const selectedEquipmentName = computed(() => {
  const eqId = Number(filters.value.equipment_id) || 0
  if (!eqId) return ''
  const eq = Array.isArray(equipmentStore.equipment)
    ? equipmentStore.equipment.find((e) => e.id === eqId)
    : null
  return eq ? eq.name : ''
})

const localTotalFuelUsed = computed(() => {
  const list = Array.isArray(filteredTransactions.value) ? filteredTransactions.value : []
  return list.reduce(
    (sum, t) => sum + (typeof t?.liters === 'number' ? t.liters : Number(t?.liters) || 0),
    0,
  )
})

const localTotalFuelCost = computed(() => {
  const list = Array.isArray(filteredTransactions.value) ? filteredTransactions.value : []
  return list.reduce((sum, t) => {
    const tc = typeof t?.total_cost === 'number' ? t.total_cost : undefined
    if (typeof tc === 'number' && Number.isFinite(tc)) return sum + tc
    const liters = typeof t?.liters === 'number' ? t.liters : Number(t?.liters) || 0
    const cpl =
      typeof t?.cost_per_liter === 'number' ? t.cost_per_liter : Number(t?.cost_per_liter) || 0
    return sum + liters * cpl
  }, 0)
})

const localAverageCostPerLiter = computed(() => {
  const liters = localTotalFuelUsed.value
  return liters > 0 ? localTotalFuelCost.value / liters : 0
})

function handlePageChange(page: number) {
  currentPage.value = page
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function handleAddTransaction() {
  showForm.value = true
  editingTransaction.value = null
}

function handleEdit(transaction: FuelTransaction) {
  console.log('[FuelTransactionList] handleEdit', transaction)
  showForm.value = true
  editingTransaction.value = transaction
}

function handleView(transaction: FuelTransaction) {
  console.log('[FuelTransactionList] handleView', transaction)
  viewTransaction.value = transaction
  showViewModal.value = true
}

async function handleDelete(payload: number | FuelTransaction) {
  const rawId = typeof payload === 'number' ? payload : payload.id
  const id = Number(rawId)
  if (!Number.isFinite(id)) return

  const confirmed = await confirm({
    title: 'Delete Fuel Transaction',
    message: 'Are you sure you want to delete this fuel transaction? This action cannot be undone.',
    confirmText: 'Delete',
    type: 'danger'
  })
  if (!confirmed) return

  try {
    await fuelStore.deleteTransaction(id)
    await fuelStore.fetchTransactions()
    notificationStore.showSuccess('Fuel transaction deleted successfully.')
  } catch (err) {
    console.error('Failed to delete transaction', err)
    const msg = err instanceof Error ? err.message : 'Failed to delete transaction'
    error.value = msg
    notificationStore.showError(msg)
  }
}

async function onRetryFetch() {
  await fuelStore.fetchTransactions()
}

const handleSave = async (data: FuelTransactionCreate) => {
  formLoading.value = true
  try {
    let result: FuelTransaction | undefined
    if (editingTransaction.value && editingTransaction.value.id) {
      result = await fuelStore.updateTransaction(editingTransaction.value.id, data)
      notificationStore.showSuccess('Fuel transaction updated successfully!')
    } else {
      result = await fuelStore.createTransaction(data)
      notificationStore.showSuccess('Fuel transaction recorded successfully!')
    }

    try {
      const exists = Array.isArray(fuelStore.transactions)
        ? fuelStore.transactions.some((t) => t.id === result?.id)
        : false
      if (!exists) {
        await fuelStore.fetchTransactions()
      }
    } catch {
      // ignore
    }

    showForm.value = false
    editingTransaction.value = null
  } catch (err) {
    console.error('Failed to save fuel transaction', err)
    const msg = err instanceof Error ? err.message : 'Failed to save fuel transaction'
    error.value = msg
    notificationStore.showError(msg)
  } finally {
    formLoading.value = false
  }
}

function applyFilters() {
  // Logic to apply selected filters
}

function resetFilters() {
  filters.value = {
    equipment_id: '',
    date_range: '',
    sort_by: 'date_desc',
  }
}

// equipmentStore.getName/ fetchNameIfMissing provide name lookups
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
